import type { PageRef } from 'providers/page-ref';

/**
 * The decisions behind the side menu and the phone bottom bar, with no Angular in
 * them (fourth slice of deferred finding df-7b66acbeb341).
 *
 * They lived on `AppComponent`, which cannot be loaded under the node test
 * runner, so none of them could be tested. The Angular half —
 * `app-navigation-menu.service.ts` — reads the navigation state and hands the
 * answers' inputs in here as plain values. Every rule below is the one the
 * component applied, condition for condition.
 */

/** One entry in the side menu. */
export interface PageInterface {
  title: string;
  icon?: string;

  id?: string;

  /// use at least one of: pageRef/url/callback - if multiple are defined, they are activated in this order
  callback?: () => void;
  url?: string;
  pageRef?: PageRef<any, any>;

  windowName?: string /** if using url - opens in a new browser tab */;

  addSeparatorAfter?: boolean;

  isVisible?: () => boolean;
  isEnabled?: () => boolean;
  isNotificationDotVisible?: () => boolean;
}

/** Pages that always hide the side menu, and any sub-pages pushed on top of them. */
export const PAGES_THAT_HIDE_SIDE_MENU = ['MealPlanPage'];

/** Pages that show the side menu even while the welcome "wizard" is still pending. */
export const PAGES_WITH_SIDE_MENU_DURING_WELCOME = ['SettingsPage', 'AppSettingsPage', 'UserProfilePage', 'HelpPage', 'SendFeedbackPage'];

/** Whether a side-menu entry is shown at all. */
export function isMenuItemVisible(page: PageInterface): boolean {
  return page.isVisible == null || page.isVisible(); // if no function is defined, visible by default
}

/** Whether a shown side-menu entry can be tapped. Training and Nutrition use this to stay locked until the full app is unlocked. */
export function isMenuItemEnabled(page: PageInterface): boolean {
  return page.isEnabled == null || page.isEnabled(); // if no function is defined, enabled by default
}

/** Whether a side-menu entry carries the small notification dot. */
export function isMenuItemNotificationDotVisible(page: PageInterface): boolean {
  return page.isNotificationDotVisible && page.isNotificationDotVisible();
}

export interface SideMenuVisibilityInput {
  /** Names of every page on the navigation stack. */
  pageStack: string[];
  /** Name of the active page, or undefined when there is none yet. */
  currentPage: string | undefined;
  /** The active page's `mode` parameter. A function because it is only read for the intake form. */
  getCurrentPageMode: () => string | undefined;
  welcomePhaseDone: boolean;
}

/** Whether the side menu is shown for the current page, navigation stack and welcome state. */
export function decideShowSideMenu(input: SideMenuVisibilityInput): boolean {
  // if any page in the navigation stack hides the menu, keep it hidden
  if (input.pageStack.some(page => PAGES_THAT_HIDE_SIDE_MENU.includes(page))) {
    return false;
  }

  // for intake-form, show menu only when editing from program-settings (mode='change')
  // hide menu in wizard flow (mode='new' or undefined - both treated as new by intake form)
  if (input.currentPage === 'IntakeFormPage') {
    if (input.getCurrentPageMode() !== 'change') {
      return false;
    }
  }

  // if initial welcome "wizard" is done, always show menu on larger screens
  if (input.welcomePhaseDone) {
    return true;
  }

  // if some welcome phase is still pending, show the menu only on the pages that
  // keep it during welcome; otherwise, show it only when explicitly invoked
  return PAGES_WITH_SIDE_MENU_DURING_WELCOME.includes(input.currentPage);
}

export interface BottomNavVisibilityInput {
  /** True while the navigation stack is being restored. Showing the bar then makes it flicker. */
  isRestoring: () => boolean;
  /** True when the screen is narrower than the collapsed side menu's breakpoint. */
  isBelowCollapsedMenuBreakpoint: () => boolean;
  /** True when the active page is one of the pages the bottom bar leads to. */
  isOnRootPage: () => boolean;
}

/** Each input is a function so the checks short-circuit in the order they always have. */
export function decideShowBottomNav(input: BottomNavVisibilityInput): boolean {
  // don't show during navigation stack restoration (prevents animation flicker)
  if (input.isRestoring()) {
    return false;
  }

  // if the screen is wide enough for the collapsed side menu, it needs no bottom nav
  if (!input.isBelowCollapsedMenuBreakpoint()) {
    return false;
  }

  // only show on root pages that are accessible from bottom nav
  return input.isOnRootPage();
}
